import { z } from 'zod';
import { conversationTagsSchema, crowdexCollaborationSchema } from '../request-schemas';

/**
 * Local-profile conversation records (`/api/local/*`). They are ordinary
 * `thread`/`message` rows on this server's machine; the shape matches what the
 * renderer has always persisted for local and worktree conversations.
 */
export const localRuntimeSchema = z.enum(['local', 'worktree']);

export const localConversationMessageSchema = z.object({
  id: z.string().min(1).max(256),
  role: z.enum(['system', 'user', 'assistant']),
  parts: z.array(z.unknown()),
  metadata: z.unknown().nullish(),
});

export const localConversationAgentSchema = z.object({
  id: z.string().min(1).max(128),
  name: z.string().min(1).max(200),
  command: z.string().min(1).max(4096),
  args: z.array(z.string()),
  env: z.record(z.string(), z.string()),
});

export const saveLocalConversationInputSchema = z.object({
  id: z.uuid(),
  workspaceId: z.string().min(1).max(128),
  workspaceRoot: z.string().min(1).max(4096),
  /** A name for its workspace when the server records it first; the server's stays after. */
  workspaceName: z.string().min(1).max(200),
  logicalWorkspaceId: z.string().max(128).nullish(),
  agentConfigId: z.string().max(128),
  agent: localConversationAgentSchema,
  title: z.string().max(500),
  runtime: localRuntimeSchema,
  collaboration: crowdexCollaborationSchema,
  modelId: z.string().max(500).nullable(),
  status: z.enum(['active', 'archived']),
  messages: z.array(localConversationMessageSchema).max(10_000),
  messageRevision: z.number().int().min(0),
  canonicalWorkspaceId: z.boolean().optional(),
  /** Labels a new conversation is created with (kept as they are afterwards). */
  tags: conversationTagsSchema.optional(),
});

export const saveLocalConversationResultSchema = z.object({
  revision: z.number().int(),
  lastMessageAt: z.string().nullable(),
  workspaceId: z.string(),
  logicalWorkspaceId: z.string().nullable(),
  /** Its workspace's folder and name as the server keeps them. */
  workspaceRoot: z.string(),
  workspaceName: z.string(),
});

export const saveLocalWorkspaceInputSchema = z.object({
  id: z.string().max(128).optional(),
  /** The project it joins; a worktree is always its repository's. */
  logicalWorkspaceId: z.string().max(128).optional(),
  rootPath: z.string().min(1).max(4096),
  /** Its name when it is new (else its folder's); the server keeps its own after. */
  name: z.string().min(1).max(200).optional(),
  /** What runs there, for a folder Git cannot read now; the server tells worktrees itself. */
  runtime: localRuntimeSchema.optional(),
});

/** Where this machine's managed worktrees go and how many cleanup keeps (Settings › Worktrees). */
export const worktreeSettingsSchema = z.object({
  /** Their folder; null: `~/.crowdex/worktrees`. */
  root: z.string().trim().max(4096).nullable(),
  autoCleanup: z.boolean(),
  keepCount: z.number().int().min(1).max(100),
});

export const localRuntimeServerSchema = z.object({
  url: z.string(),
  token: z.string(),
});

export type SaveLocalConversationInput = z.infer<typeof saveLocalConversationInputSchema>;
export type SaveLocalConversationResult = z.infer<typeof saveLocalConversationResultSchema>;
export type SaveLocalWorkspaceInput = z.infer<typeof saveLocalWorkspaceInputSchema>;
export type WorktreeSettings = z.infer<typeof worktreeSettingsSchema>;

export const DEFAULT_WORKTREE_SETTINGS: WorktreeSettings = {
  root: null,
  autoCleanup: true,
  keepCount: 15,
};

/** The conversations pinned to the top of the sidebar, in order (`/api/conversations/pins`). */
export const conversationPinsSchema = z.object({
  ids: z.array(z.string().min(1).max(128)).max(500),
});
